import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { format } from 'date-fns';
import supabase from '../../../supabase/supabase';
import SafeIcon from '../../../common/SafeIcon';
import { FiDollarSign, FiUsers, FiTrendingUp, FiDownloadCloud, FiRefreshCw } from 'react-icons/fi';

// Fallback Data based on the provided prompt structure
const MOCK_DATA = {
  "range": {
    "from": "2025-07-01T00:00:00Z",
    "to": "2026-06-30T23:59:59Z"
  },
  "revenue_by_month": {
    "currency": "USD",
    "points": [
      { "month": "2025-07", "amount": 125000, "amount_display": 1250.0 },
      { "month": "2025-08", "amount": 98000,  "amount_display": 980.0 },
      { "month": "2025-09", "amount": 143500, "amount_display": 1435.0 }
    ]
  },
  "users": {
    "total_users": 1240,
    "total_paid_users": 312,
    "paid_users": [
      {
        "customer_id": "cus_abc123",
        "name": "Aarav Mehta",
        "email": "aarav@example.com",
        "first_paid_at": "2025-09-10T11:20:00Z",
        "last_paid_at": "2026-06-20T08:35:00Z",
        "payment_count": 7,
        "lifetime_revenue": {
          "currency": "USD",
          "amount": 45900,
          "amount_display": 459.0
        }
      },
      {
        "customer_id": "cus_def456",
        "name": "Sarah Jenkins",
        "email": "sarah.j@acme.co",
        "first_paid_at": "2025-07-15T09:10:00Z",
        "last_paid_at": "2026-06-15T14:20:00Z",
        "payment_count": 12,
        "lifetime_revenue": {
          "currency": "USD",
          "amount": 96000,
          "amount_display": 960.0
        }
      },
      {
        "customer_id": "cus_ghi789",
        "name": "Marcus Chen",
        "email": "marcus@saasify.io",
        "first_paid_at": "2025-08-01T16:45:00Z",
        "last_paid_at": "2026-05-01T10:05:00Z",
        "payment_count": 10,
        "lifetime_revenue": {
          "currency": "USD",
          "amount": 80000,
          "amount_display": 800.0
        }
      }
    ]
  }
};

const StatCard = ({ title, value, icon, trend }) => (
  <div className="p-6 rounded-2xl bg-zinc-900/40 backdrop-blur-md border border-orange-500/10 shadow-sm relative overflow-hidden">
    <div className="absolute -right-6 -top-6 w-24 h-24 bg-orange-500/5 rounded-full blur-2xl pointer-events-none"></div>
    <div className="flex justify-between items-start mb-4 relative z-10">
      <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center border border-orange-500/20 text-orange-400">
        <SafeIcon icon={icon} className="text-lg" />
      </div>
      {trend && (
        <span className="text-xs font-semibold px-2 py-1 rounded-md bg-green-500/10 text-green-400 border border-green-500/20 flex items-center gap-1">
          <SafeIcon icon={FiTrendingUp} /> {trend}
        </span>
      )}
    </div>
    <div className="relative z-10">
      <h3 className="text-sm font-medium text-zinc-400 mb-1">{title}</h3>
      <div className="text-3xl font-bold text-amber-50">{value}</div>
    </div>
  </div>
);

const Reports = () => {
  const { setHeaderAction } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchReportData = useCallback(async (forceRefresh = false) => {
    if (forceRefresh) setIsRefreshing(true);
    else setLoading(true);

    try {
      const today = new Date().toDateString();
      const cachedDate = localStorage.getItem('mot_reports_date');
      const cachedData = localStorage.getItem('mot_reports_data');

      // Use cache if not forcing and we already fetched today
      if (!forceRefresh && cachedDate === today && cachedData) {
        setData(JSON.parse(cachedData));
        setLoading(false);
        return;
      }

      // Invoke the Edge Function per user request
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        throw new Error('You must be logged in to view reports.');
      }
      const { data: funcData, error: funcError } = await supabase.functions.invoke('dodo-data', {
        body: {},
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      
      if (funcError || !funcData) {
        console.warn("Edge function 'dodo-data' failed or returned no data. Using fallback.", funcError);
        // Fallback to cache if available, else MOCK_DATA
        if (cachedData) {
          setData(JSON.parse(cachedData));
        } else {
          setData(MOCK_DATA);
        }
      } else {
        // Success: Update state and cache
        setData(funcData);
        localStorage.setItem('mot_reports_data', JSON.stringify(funcData));
        localStorage.setItem('mot_reports_date', today);
      }
    } catch (err) {
      console.error("Error fetching report data:", err);
      const cachedData = localStorage.getItem('mot_reports_data');
      if (cachedData) setData(JSON.parse(cachedData));
      else setData(MOCK_DATA);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Set the header action dynamically when component mounts or refreshing state changes
  useEffect(() => {
    setHeaderAction(
      <button 
        onClick={() => fetchReportData(true)}
        disabled={isRefreshing}
        className="px-4 py-2 bg-gradient-to-r from-orange-500/10 to-transparent hover:from-orange-500/20 text-amber-100 text-sm font-medium rounded-xl border border-orange-500/20 flex items-center gap-2 transition-all duration-300 shadow-sm disabled:opacity-50"
      >
        <SafeIcon icon={FiRefreshCw} className={isRefreshing ? "animate-spin text-orange-400" : "text-orange-400"} />
        <span className="hidden sm:inline">{isRefreshing ? 'Syncing...' : 'Refresh Data'}</span>
      </button>
    );

    // Cleanup when component unmounts
    return () => setHeaderAction(null);
  }, [fetchReportData, isRefreshing, setHeaderAction]);

  useEffect(() => {
    fetchReportData(false);
  }, [fetchReportData]);

  if (loading) {
    return (
      <div className="py-20 flex justify-center w-full">
        <div className="w-10 h-10 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="py-20 text-center text-red-400">
        Failed to load report data.
      </div>
    );
  }

  // Calculate aggregate values
  const totalRevenue = data.revenue_by_month.points.reduce((acc, pt) => acc + pt.amount_display, 0);
  
  // Sort users by highest lifetime revenue
  const sortedUsers = [...data.users.paid_users].sort(
    (a, b) => b.lifetime_revenue.amount_display - a.lifetime_revenue.amount_display
  );

  // Echarts configuration
  const chartOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(24, 24, 27, 0.9)',
      borderColor: 'rgba(234, 88, 12, 0.2)',
      textStyle: { color: '#fef3c7', fontFamily: 'Plus Jakarta Sans, sans-serif' },
      formatter: (params) => {
        const val = params[0].value;
        return `<div class="font-medium mb-1">${params[0].name}</div>
                <div class="text-orange-400 font-bold text-lg">$${val.toLocaleString(undefined, {minimumFractionDigits: 2})}</div>`;
      }
    },
    grid: { 
      left: '2%', 
      right: '2%', 
      bottom: '5%', 
      top: '10%', 
      containLabel: true 
    },
    xAxis: {
      type: 'category',
      data: data.revenue_by_month.points.map(p => p.month),
      axisLine: { lineStyle: { color: 'rgba(255,255,255,0.1)' } },
      axisLabel: { color: '#a1a1aa', fontFamily: 'Plus Jakarta Sans, sans-serif', margin: 12 },
      axisTick: { show: false }
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: 'rgba(255,255,255,0.05)', type: 'dashed' } },
      axisLabel: { 
        color: '#a1a1aa', 
        fontFamily: 'Plus Jakarta Sans, sans-serif',
        formatter: '${value}' 
      }
    },
    series: [
      {
        name: 'Revenue',
        type: 'bar',
        barMaxWidth: 48,
        data: data.revenue_by_month.points.map(p => p.amount_display),
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#f59e0b' }, // yellow-500
            { offset: 1, color: '#ea580c' }  // orange-600
          ]),
          borderRadius: [6, 6, 0, 0]
        },
        emphasis: {
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#fcd34d' }, // yellow-200
              { offset: 1, color: '#f97316' }  // orange-500
            ])
          }
        }
      }
    ]
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-6xl mx-auto pb-12"
    >
      {/* Top Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 mt-2">
        <StatCard 
          title="Total Revenue (Period)" 
          value={`$${totalRevenue.toLocaleString(undefined, {minimumFractionDigits: 2})}`} 
          icon={FiDollarSign} 
        />
        <StatCard 
          title="Total Paid Users" 
          value={data.users.total_paid_users.toLocaleString()} 
          icon={FiUsers} 
        />
        <StatCard 
          title="Total Platform Users" 
          value={data.users.total_users.toLocaleString()} 
          icon={FiUsers} 
        />
      </div>

      {/* Revenue Graph Card */}
      <div className="p-6 rounded-2xl bg-zinc-900/40 backdrop-blur-md border border-orange-500/10 shadow-sm mb-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h3 className="text-lg font-semibold text-amber-50 mb-1">Revenue Overview</h3>
            <p className="text-sm text-zinc-400">Monthly revenue generated via Dodo Payments.</p>
          </div>
          <button className="px-3 py-1.5 bg-zinc-800/50 hover:bg-zinc-800 text-zinc-300 text-xs font-medium rounded-lg border border-white/5 flex items-center gap-2 transition-colors">
            <SafeIcon icon={FiDownloadCloud} /> Export CSV
          </button>
        </div>
        
        <div className="h-[350px] w-full">
          <ReactECharts 
            option={chartOption} 
            style={{ height: '100%', width: '100%' }}
            opts={{ renderer: 'svg' }}
          />
        </div>
      </div>

      {/* Leaderboard Table Card */}
      <div className="p-6 rounded-2xl bg-zinc-900/40 backdrop-blur-md border border-orange-500/10 shadow-sm">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-amber-50 mb-1">Top Customers</h3>
          <p className="text-sm text-zinc-400">Paid users sorted by highest lifetime value (LTV).</p>
        </div>

        <div className="overflow-x-auto rounded-xl border border-white/5">
          <table className="w-full text-left border-collapse whitespace-nowrap">
            <thead className="bg-[#0a0500]/50 border-b border-orange-500/10">
              <tr>
                <th className="py-4 px-5 text-xs font-semibold tracking-wider text-zinc-500 uppercase">Customer</th>
                <th className="py-4 px-5 text-xs font-semibold tracking-wider text-zinc-500 uppercase">First Paid</th>
                <th className="py-4 px-5 text-xs font-semibold tracking-wider text-zinc-500 uppercase">Last Paid</th>
                <th className="py-4 px-5 text-xs font-semibold tracking-wider text-zinc-500 uppercase text-right">Payments</th>
                <th className="py-4 px-5 text-xs font-semibold tracking-wider text-zinc-500 uppercase text-right">Lifetime Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 bg-zinc-950/20">
              {sortedUsers.map((user) => (
                <tr key={user.customer_id} className="hover:bg-orange-500/5 transition-colors group">
                  <td className="py-4 px-5">
                    <div className="text-amber-50 font-medium group-hover:text-orange-300 transition-colors">{user.name}</div>
                    <div className="text-xs text-zinc-500">{user.email}</div>
                  </td>
                  <td className="py-4 px-5 text-sm text-zinc-400">
                    {format(new Date(user.first_paid_at), 'MMM d, yyyy')}
                  </td>
                  <td className="py-4 px-5 text-sm text-zinc-400">
                    {format(new Date(user.last_paid_at), 'MMM d, yyyy')}
                  </td>
                  <td className="py-4 px-5 text-sm text-zinc-400 text-right">
                    <span className="px-2.5 py-1 bg-zinc-800 rounded-md border border-zinc-700 font-medium">
                      {user.payment_count}
                    </span>
                  </td>
                  <td className="py-4 px-5 text-sm font-semibold text-orange-400 text-right">
                    ${user.lifetime_revenue.amount_display.toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
};

export default Reports;